import {getUserPremiumLevel, isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {openMessageModal} from '@Modals/message-modal';
import {isMobile} from 'react-device-detect';
import {openResizeModal} from '@Modals/resize-modal';
import {openTableModal} from '@Modals/user-table-modal';
import type {TableItem} from '@PosterWhiteboard/items/table-item/table-item.class';
import {addScheduleToPoster} from '@PosterWhiteboard/items/table-item/table-item.class';
import {DEFAULT_COLUMNS, DEFAULT_ROWS, getDefaultScheduleData, getDefaultTableData, getScheduleData, getTableData} from '@PosterWhiteboard/items/table-item/user-table';
import {openScheduleModal} from '@Modals/user-schedule-modal/user-schedule-modal';
import {LayoutData} from '@Panels/user-schedule-panel/user-schedule-panel.types';
import type {MenuItem} from '@PosterWhiteboard/items/menu-item/menu-item.class';
import {addMenuToPoster} from '@PosterWhiteboard/items/menu-item/menu-item.class';
import {openUserMenuModal} from '@Modals/user-menu-modal';
import {getSelectedMenuItems} from '@PosterWhiteboard/items/menu-item/user-menu';
import {openFancytextModal} from '@Modals/fancytext-modal/fancytext-modal';
import type {AudioData, ElementData, ImageData, PMWExtractedGettyStickerData, PMWStillStickerData, TableData, TempImageData, VideoData} from '@Libraries/add-media-library';
import {ElementDataType, isStickerElementType} from '@Libraries/add-media-library';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import {openTrimVideoModal} from '@Modals/trim-video-modal';
import {repoVideoURLForModel} from '@Libraries/user-video-library';
import type {TrimData} from '@Panels/trim-panels/trim-panels.types';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {openTrimAudioModal} from '@Modals/trim-audio-modal';
import {getAudioUrl} from '@Libraries/user-audio-library';
import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import {openCropperModal} from '@Modals/cropper-modal/cropper-modal.library';
import type {CropperPanelApplyResponse} from '@Panels/cropper-panel';
import {openVideoCropperModal} from '@Modals/video-cropper-modal/video-cropper-modal.types';
import type {VideoCropperPanelApplyResponse} from '@Panels/video-cropper-panel/video-cropper-panel.types';
import {ReplaceMediaControlsButtons} from '@Components/replace-media-controls';
import {onAddBrandAssetClickedHandler, removeVideoBackgroundFromId, removeVideoSlideBackgroundFromId} from '@Components/poster-editor/library/poster-editor-library';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';
import {loadImageAsync} from '@Utils/image.util';
import type {MaskingObject} from '@PosterWhiteboard/classes/masking/masking.class';
import {doesPublicFileExistAsync} from '@Utils/file.util';
import {openMaskingModal} from '@Modals/masking-modal';
import {openColorBackgroundModal} from '@Modals/color-background-modal';
import type {BackgroundColorType} from '@Panels/color-background-panel/color-background-type.types';
import {BACKGROUND_TYPE, GRADIENT_TYPE} from '@Panels/color-background-panel/color-background-type.types';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {RGBA} from '@Utils/color.util';
import {hexToRgba, lightenDarkenColor, rgbaToHex} from '@Utils/color.util';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {POSTER_GETTY_LIMIT} from '@PosterWhiteboard/poster/poster.types';
import {LayoutTypes} from '@PosterWhiteboard/items/layouts/layout.types';
import {DEFAULT_BACKGROUND_COLOR} from '@PosterWhiteboard/page/page.types';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import type {CopyAndResizeData, ResizeModalPosterInformation} from '@Panels/resize-panel/resize-panel.types';
import {openStockMediaModal} from '@Modals/stock-modal';
import {openPosterEditorLanguageModal} from '@Modals/poster-editor-language-modal';
import type {UserTableProps} from '@Panels/user-table-panel/user-table-panel';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import type {BuyCreditsResponse} from '@/types/credits.types';
import {openSettingsModal} from '@Modals/settings-modal';
import {openConfirmRemoveVideoBackgroundModal} from '@Modals/confirm-remove-video-background-modal';
import {refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {updateFeatureDemo, updateWebDownloadPanelOpenState} from '@Components/poster-editor/poster-editor-reducer';
import {FeatureDemo} from '@Components/poster-editor/poster-editor.types';
import {isPremiumUser} from '@Utils/user.util';
import {openResumableStockMediaMoal} from '@Modals/resumable-stock-modal/resumable-stock-modal';
import {DownloadPublishModalMode} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options/nav-right-options.types';
import {openUploadingImagesModal} from '@Modals/uploading-images-modal';
import {copyAndResizePoster, getWordFromUnits, resizePosterForResizeData, SizeUnits} from '@/poster-whiteboard/libraries/resize-poster.library';
import {PMWStockImageSource} from '@Libraries/pmw-stock-media-library.types';
import {createKeepFiltersHiddenForTab, getAnimatedStickerSources} from '@Panels/stock-panel/stock-panel.library';
import {openSharingModal} from '@Modals/share-modal/share-modal-library';
import {SHARE_PANEL_ID, ShareItemType} from '@Panels/share-panel/share-panel.types';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {isEditorMobileVariant} from '@Components/poster-editor/library/is-editor-mobile-variant';
import {isColorBackground, isVideoBackground} from '@PosterWhiteboard/page/background/background.types';
import {openMultipagePreviewThumbnailSelectionModal} from '@Modals/multipage-preview-thumbnail-selection-modal/multipage-preview-thumbnail-selection-modal.library';
import {openNewDownloadModal} from '@Modals/download-modal/download-modal.library';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.types';

interface BuyMoreCreditsParams {
  headingText?: string;
  modalTitle?: string;

  cb(credits?: number): void;
}

const hasStickerItem = (elementsData: (ItemData | TempImageData)[]): boolean => elementsData.some((el) => 'type' in el && isStickerElementType(el.type as ElementDataType));

export const onAddItems = (elementsData: (ItemData | TempImageData)[]): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }

  const featureDemo = window.PMW.redux.store.getState().posterEditor.featureDemo;
  if (featureDemo === FeatureDemo.STICKERS && hasStickerItem(elementsData)) {
    trackPosterBuilderGA4Events(GA4EventName.EDITOR_TRY_STICKER, {[GA4EventParamName.USER_PREMIUM_LEVEL]: String(getUserPremiumLevel())});
    window.PMW.redux.store.dispatch(updateFeatureDemo(FeatureDemo.NONE));
  }

  void currentPage.items.addItems.addNewItems(elementsData);
};

const onAddAudioItems = (audioItemsData: AudioData[]): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }

  void currentPage.items.addItems.addAudioItems(audioItemsData, {});
  trackPosterBuilderGA4Events(GA4EventName.ADD_OWN_AUDIO);
};

export const onReplaceAudioItem = (audioItemIdToReplace: string, audioItemsData: AudioData[]): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage || audioItemsData.length === 0) {
    return;
  }

  void window.posterEditor?.whiteboard?.getCurrentPage().audioClips.replaceAudioItem(audioItemIdToReplace, audioItemsData[0]);
};

export const onAddMediaSlideshow = (elementsData: (ImageData | TempImageData | VideoData | PMWStillStickerData | PMWExtractedGettyStickerData)[]): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  void currentPage?.items.addItems.onAddMediaSlideshow(elementsData);
};

export const addImageBackground = (elementsData: (ImageData | TempImageData)[]): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  currentPage.items.addItems.onAddImageBackground(elementsData);
};

export const addVideoBackground = (videoData: VideoData): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  currentPage.items.addItems.onAddVideoBackground(videoData);
};

export const addBackground = (elementsData: ElementData[]): void => {
  const backgroundData = elementsData[0];
  if (backgroundData.type === ElementDataType.VIDEO) {
    addVideoBackground(backgroundData);
  } else {
    addImageBackground(elementsData as (ImageData | TempImageData)[]);
  }
};

export const onOpenUserMediaModal = (): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  openUserMediaModal({
    onAddSelected: onAddItems,
    shouldLoadDataUrlImage: true,
    onAddBrandAsset: (): void => {
      void onAddBrandAssetClickedHandler();
    },
    defaultTab: UploadsMediaType.IMAGE,
  });
};

export const onOpenSlideshowUserMediaModal = (): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  openUserMediaModal({
    onAddSelected: onAddMediaSlideshow,
    shouldLoadDataUrlImage: true,
    onAddBrandAsset: (): void => {
      void onAddBrandAssetClickedHandler();
    },
    addInBulk: true,
    showVectors: false,
    uploadAcceptedMimetype: `image/jpeg,image/heic,image/heif,image/png,image/webp,image/gif,video/quicktime,video/mp4,video/x-m4v,video/*`,
    mediaTypesToShow: [UploadsMediaType.IMAGE, UploadsMediaType.VIDEO],
    defaultTab: UploadsMediaType.IMAGE,
  });
};

export const onOpenSlideshowStockMediaModal = (onAddSelected?: (elementsData: (ImageData | VideoData)[]) => void): void => {
  openStockMediaModal({
    showTextToImageTool: true,
    blacklistedSources: getAnimatedStickerSources(),
    onAddSelected: onAddSelected ?? onAddMediaSlideshow,
    tabsToShow: [StockPanelTabs.PHOTOS, StockPanelTabs.VIDEOS, StockPanelTabs.TEXT_TO_IMAGE, StockPanelTabs.STICKERS],
    addInBulk: !onAddSelected,
    shouldLoadDataUrlImage: true,
    hideOnClose: false,
    panelId: 'STOCK_MEDIA_VIA_SLIDESHOW',
    keepFiltersHiddenForTab: createKeepFiltersHiddenForTab([StockPanelTabs.STICKERS]),
  });
};

export const onOpenUserVideoBackgroundUploadsModal = (): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }

  openUserMediaModal({
    onAddSelected: addBackground,
    shouldLoadDataUrlImage: true,
    onAddBrandAsset: (): void => {
      void onAddBrandAssetClickedHandler();
    },
    mediaTypesToShow: [UploadsMediaType.VIDEO_BACKGROUND],
    maxSelectionNumber: 1,
    showVectors: false,
    uploadAcceptedMimetype: 'video/quicktime,video/mp4,video/x-m4v,video/*',
    defaultTab: UploadsMediaType.VIDEO_BACKGROUND,
  });
};

export const onOpenUserImageBackgroundUploadsModal = (): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  openUserMediaModal({
    onAddSelected: addBackground,
    shouldLoadDataUrlImage: true,
    onAddBrandAsset: (): void => {
      void onAddBrandAssetClickedHandler();
    },
    mediaTypesToShow: [UploadsMediaType.IMAGE_BACKGROUND],
    maxSelectionNumber: 1,
    showVectors: false,
    uploadAcceptedMimetype: 'image/jpeg,image/heic,image/heif,image/png,image/webp,image/gif',
    defaultTab: UploadsMediaType.IMAGE_BACKGROUND,
  });
};

export const onOpenUserBackgroundUploadsModal = (): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  openUserMediaModal({
    onAddSelected: addBackground,
    shouldLoadDataUrlImage: true,
    onAddBrandAsset: (): void => {
      void onAddBrandAssetClickedHandler();
    },
    mediaTypesToShow: [UploadsMediaType.IMAGE_BACKGROUND, UploadsMediaType.VIDEO_BACKGROUND],
    maxSelectionNumber: 1,
    showVectors: false,
    uploadAcceptedMimetype: 'image/jpeg,image/heic,image/heif,image/png,image/webp,image/gif,video/quicktime,video/mp4,video/x-m4v,video/*',
    defaultTab: UploadsMediaType.IMAGE_BACKGROUND,
  });
};

export const onOpenStockBackgroundModal = (): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  openStockMediaModal({
    onAddSelected: addImageBackground,
    shouldLoadDataUrlImage: true,
    tabsToShow: [StockPanelTabs.PHOTOS],
    defaultSearchTab: StockPanelTabs.PHOTOS,
    maxSelectionNumber: 1,
    hideOnClose: false,
    panelId: 'STOCK_MEDIA_VIA_BACKGROUND',
  });
};

export const onOpenStockVideoBackgroundModal = (): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  openStockMediaModal({
    onAddSelected: addBackground,
    tabsToShow: [StockPanelTabs.VIDEOS],
    defaultSearchTab: StockPanelTabs.VIDEOS,
    maxSelectionNumber: 1,
    hideOnClose: false,
    panelId: 'STOCK_MEDIA_VIA_VIDEO_BACKGROUND',
  });
};

export const onOpenStockMediaModal = (defaultSearchTab?: StockPanelTabs): void => {
  const poster = window.posterEditor?.whiteboard;
  if (poster?.mode.isRegen()) {
    openMessageModal({
      title: window.i18next.t('pmwjs_add_stock_image_disabled_title'),
      text: window.i18next.t('pmwjs_add_stock_image_disabled_message'),
    });
    return;
  }

  openResumableStockMediaMoal({
    bypassCompoundedView: true,
    onAddSelected: onAddItems,
    shouldLoadDataUrlImage: true,
    showTextToImageTool: true,
    hideOnClose: true,
    panelId: 'RESUMABLE_STOCK_MEDIA_PANEL',
    tabId: 'RESUMABLE_STOCK_MEDIA_TAB_ID',
    ...(defaultSearchTab !== undefined && {defaultSearchTab}),
    stockTabToGridIdMapping: {
      [StockPanelTabs.ALL]: 'resumable-stock-grid-all',
      [StockPanelTabs.PHOTOS]: 'resumable-stock-grid-photos',
      [StockPanelTabs.TEXT_TO_IMAGE]: 'resumable-stock-grid-t2i',
      [StockPanelTabs.VIDEOS]: 'resumable-stock-grid-videos',
      [StockPanelTabs.SHAPES]: 'resumable-stock-grid-shapes',
      [StockPanelTabs.AUDIOS]: 'resumable-stock-grid-audios',
      [StockPanelTabs.STICKERS]: 'resumable-stock-grid-stickers',
      [StockPanelTabs.ICONS]: 'resumable-stock-grid-icons',
    },
  });
};

export const onOpenUserAudioModal = (): void => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }

  openUserMediaModal({
    onAddSelected: onAddAudioItems,
    defaultTab: UploadsMediaType.AUDIO,
    mediaTypesToShow: [UploadsMediaType.AUDIO],
    uploadAcceptedMimetype: 'audio/mp3,audio/*',
  });
};

export const onOpenUserAudioUploadModalForReplace = (audioItemIdToReplace: string): void => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }

  openUserMediaModal({
    onAddSelected: (items: AudioData[]) => {
      onReplaceAudioItem(audioItemIdToReplace, items);
    },
    onAddBrandAsset: (): void => {
      void onAddBrandAssetClickedHandler();
    },
    maxSelectionNumber: 1,
    defaultTab: UploadsMediaType.AUDIO,
    mediaTypesToShow: [UploadsMediaType.AUDIO],
    uploadAcceptedMimetype: 'audio/mp3,audio/*',
  });
};

export const onOpenStockAudioModalForReplace = (audioItemIdToReplace: string): void => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }

  if (poster.mode.isRegen()) {
    openMessageModal({
      title: window.i18next.t('pmwjs_add_stock_audio_disabled_title'),
      text: window.i18next.t('pmwjs_add_stock_audio_disabled_message'),
    });
    return;
  }

  openStockMediaModal({
    onAddSelected: onReplaceAudioItem.bind(null, audioItemIdToReplace),
    tabsToShow: [StockPanelTabs.AUDIOS],
    defaultSearchTab: StockPanelTabs.AUDIOS,
    hideOnClose: false,
    panelId: 'STOCK_MEDIA_VIA_AUDIO_REPLACE',
  });
};

export const onOpenReplaceAudio = (type: ReplaceMediaControlsButtons, audioItemIdToReplace: string): void => {
  switch (type) {
    case ReplaceMediaControlsButtons.EXPLORE_MEDIA: {
      onOpenStockAudioModalForReplace(audioItemIdToReplace);
      return;
    }
    case ReplaceMediaControlsButtons.MY_UPLOADS: {
      onOpenUserAudioUploadModalForReplace(audioItemIdToReplace);
      return;
    }
    default:
      throw new Error('unhandled type of id received from replace media controls');
  }
};

export const openTrimAudioModalForSelectedAudioItem = (): void => {
  const currentAudioItem = window.posterEditor?.whiteboard?.getCurrentPage().audioClips.getSelectedAudioItem();
  if (!window.posterEditor?.whiteboard || !currentAudioItem) {
    return;
  }

  openTrimAudioModal({
    duration: currentAudioItem.audioPlayer.originalDuration,
    start: currentAudioItem.audioPlayer.getTrimmedStartTime(),
    end: currentAudioItem.audioPlayer.getTrimmedEndTime(),
    source: getAudioUrl(currentAudioItem.hashedFilename, currentAudioItem.source),
    audioTitle: currentAudioItem.name,
    onTrim: (trimData) => {
      trackPosterBuilderGA4Events(GA4EventName.TRIM_AUDIO_MODAL, {
        [GA4EventParamName.TYPE]: isEditorMobileVariant() ? 'mobile' : 'web',
      });
      void window.posterEditor?.whiteboard?.getCurrentPage().audioClips.trimAudioItem(currentAudioItem.uid, trimData.startTime, trimData.endTime);
    },
    maxDuration: currentAudioItem.audioPlayer.originalDuration,
  });
};

export const openGettyImageLimitReachedMessageModal = (): void => {
  trackPosterBuilderGA4Events(GA4EventName.GETTY_IMAGE_LIMIT_REACHED);
  openMessageModal({
    title: window.i18next.t('pmwjs_getty_limit'),
    text: window.i18next.t('pmwjs_getty_limit_message', {
      limit: POSTER_GETTY_LIMIT.IMAGES,
    }),
  });
};

export const openExtractedGettyStickerLimitReachedMessageModal = (): void => {
  trackPosterBuilderGA4Events(GA4EventName.EXTRACTED_GETTY_STICKER_LIMIT_REACHED);
  openMessageModal({
    title: window.i18next.t('pmwjs_extract_getty_stickers_limit'),
    text: window.i18next.t('pmwjs_extract_getty_stickers_limit_message', {
      limit: POSTER_GETTY_LIMIT.EXTRACTED_GETTY_STICKER,
    }),
  });
};

export const openGettyVideoLimitReachedMessageModal = (): void => {
  trackPosterBuilderGA4Events(GA4EventName.GETTY_VIDEO_LIMIT_REACHED);
  openMessageModal({
    title: window.i18next.t('pmwjs_getty_video_limit'),
    text: window.i18next.t('pmwjs_video_getty_limit_message', {
      num: POSTER_GETTY_LIMIT.VIDEOS,
    }),
  });
};

export const openGettyImageLimitExceededOnPageDuplicateModal = (): void => {
  openMessageModal({
    title: window.i18next.t('pmwjs_getty_limit'),
    text: window.i18next.t('pmwjs_getty_limit_exceeded_on_duplicate_message', {
      limit: POSTER_GETTY_LIMIT.IMAGES,
    }),
  });
};

export const openGettyVideoLimitExceededOnPageDuplicateModal = (): void => {
  openMessageModal({
    title: window.i18next.t('pmwjs_getty_video_limit'),
    text: window.i18next.t('pmwjs_getty_video_limit_exceeded_on_duplicate_message', {
      limit: POSTER_GETTY_LIMIT.VIDEOS,
    }),
  });
};

export const openExtractedGettyStickerLimitExceededOnPageDuplicateModal = (): void => {
  openMessageModal({
    title: window.i18next.t('pmwjs_extract_getty_stickers_limit'),
    text: window.i18next.t('pmwjs_extract_getty_stickers_limit_exceeded_on_duplicate_message', {
      limit: POSTER_GETTY_LIMIT.EXTRACTED_GETTY_STICKER,
    }),
  });
};

export const openGettyMultipleAssetsLimitExceededOnPageDuplicateModal = (): void => {
  openMessageModal({
    title: window.i18next.t('pmwjs_getty_assets_limit'),
    text: window.i18next.t('pmwjs_getty_assets_limit_exceeded_on_duplicate_message'),
  });
};

export const onOpenStockAudioModal = (): void => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }

  if (poster.mode.isRegen()) {
    openMessageModal({
      title: window.i18next.t('pmwjs_add_stock_audio_disabled_title'),
      text: window.i18next.t('pmwjs_add_stock_audio_disabled_message'),
    });
    return;
  }

  openStockMediaModal({
    onAddSelected: onAddAudioItems,
    tabsToShow: [StockPanelTabs.AUDIOS],
    defaultSearchTab: StockPanelTabs.AUDIOS,
    hideOnClose: false,
    panelId: 'STOCK_MEDIA_VIA_AUDIO',
  });
};

export const onOpenResizeModal = (): void => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }

  const getPosterInformationText = (): ResizeModalPosterInformation | undefined => {
    if (!window.PMW.redux.store.getState().posterEditor.isMobileVariant) {
      return undefined;
    }

    const showCustomSize = window.posterEditor?.whiteboard?.type.isCustomSize() && window.posterEditor?.whiteboard?.type.idUserPosterType === 0;
    let subText = '';
    if (showCustomSize) {
      if (poster.width && poster.units && poster.height) {
        subText = `${poster.width}${poster.units} x ${poster.height}${poster.units}`;
      }
    } else if (poster.type.description) {
      subText = poster.type.description;
    }

    return {
      posterType: window.PMW.redux.store.getState().posterEditor.posterObject?.type.displayName,
      posterDimensionText: subText,
    };
  };

  openResizeModal({
    allowSaveCustomSize: isUserLoggedIn(),
    allowMultiSelect: !isMobile && !window.PMW.isEmbedded(),
    unsavedCustomDimension: {
      width: poster.userWidth,
      height: poster.userHeight,
      units: getWordFromUnits(poster.units),
    },
    onResizeCallback: resizePosterForResizeData,
    onCopyAndResize: (data: CopyAndResizeData): void => {
      void copyAndResizePoster(data);
    },
    currentUnit: SizeUnits.PX,
    currentHeight: poster.height,
    currentWidth: poster.width,
    posterInformation: getPosterInformationText(),
  });
};

export const onOpenSettingsModal = (): void => {
  openSettingsModal({
    onLangChange: async (): Promise<void> => {
      if (window.posterEditor?.whiteboard?.savePoster.hasUnsavedChanges()) {
        await window.posterEditor?.whiteboard?.savePoster.save();
      }
    },
    onOpenAccessibilityMenu: (): void => {
      window.PMW.initAccessibilityMenu();
    },
  });
};

export const onOpenLanguageModal = (): void => {
  openPosterEditorLanguageModal({
    panelId: 'poster-editor-language-panel',
    onLangChange: async (): Promise<void> => {
      if (window.posterEditor?.whiteboard?.savePoster.hasUnsavedChanges()) {
        await window.posterEditor?.whiteboard?.savePoster.save();
      }
    },
  });
};

const addTableToPoster = (tableData: UserTableProps): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  void currentPage.items.addItems.addTableItem(
    {
      type: ElementDataType.TABLE,
      ...tableData,
    } as TableData,
    {},
    true
  );
};

export const onOpenTableModal = (): void => {
  const data = getDefaultTableData();
  openTableModal({
    isNew: true,
    initialTableData: data,
    initialRows: DEFAULT_ROWS,
    initialColumns: DEFAULT_COLUMNS,
    highlightedRows: [],
    onAddTable: addTableToPoster,
  });
};

export const openEditTableContentModal = (activeItem: TableItem): void => {
  if (activeItem.isSchedule()) {
    openEditScheduleModal(activeItem);
  } else {
    openEditTableModal(activeItem);
  }
};

export const openEditTableModal = (tableItem: TableItem): void => {
  const data = getTableData(tableItem.layoutDataMap, tableItem.rows);

  openTableModal({
    isNew: !tableItem,
    initialRows: tableItem.rows,
    initialColumns: tableItem.columns,
    onAddTable: tableItem.updateFromTableData.bind(tableItem),
    highlightedRows: data.highlightedRows,
    initialTableData: data.tableData,
  });
};

export const openEditScheduleModal = (tableItem: TableItem): void => {
  const data = getScheduleData(tableItem);

  openScheduleModal({
    isNew: !tableItem,
    unusedData: data.unusedData,
    timeFormat: data.timeFormat,
    dateFormat: data.dateFormat,
    initialColumns: data.columns,
    columnsData: data.columnData,
    initialTableData: data.tableData,
    highlightedRows: data.highlightedRows,
    initialRows: tableItem.rows,
    layout: tableItem.layoutStyle as unknown as LayoutData,
    onAddSchedule: tableItem.updateFromScheduleData.bind(tableItem),
  });
};

export const openEditMenuModal = (menuItem: MenuItem): void => {
  openUserMenuModal({
    layout: menuItem.layoutStyle,
    selectedIds: menuItem.itemIds.map((id) => {
      return id;
    }),
    selectedItems: getSelectedMenuItems(menuItem),
    onAddMenuItem: menuItem.updateFromMenuData.bind(menuItem),
  });
};

export const openCollaboratorsModal = (): void => {
  void window.posterEditor?.whiteboard?.savePoster.save().then(() => {
    const poster = window.posterEditor?.whiteboard;
    openSharingModal({
      itemHashedId: poster?.hashedID ?? '',
      type: ShareItemType.POSTER,
      panelId: SHARE_PANEL_ID,
    });
  });
};

export const onOpenDownloadModal = (mode = DownloadPublishModalMode.DOWNLOAD, option = ''): void => {
  const poster = window.posterEditor?.whiteboard;
  if (poster) {
    const doesPosterHaveUploadingImages = poster.areAnyImagesUploading();
    const imageSrc = poster.getImageSrcOfUploadingImage();

    if (doesPosterHaveUploadingImages && imageSrc) {
      openUploadingImagesModal({
        title: window.i18next.t('pmwjs_uploading_images'),
        text: window.i18next.t('pmwjs_uploading_images_download_modal_description'),
        imgSrc: imageSrc,
      });
    } else {
      openDownloadModal(mode, option);
    }
  } else {
    openDownloadModal(mode, option);
  }
};

export const openNewDownloadDialog = (): void => {
  const posterObject = window.posterEditor?.whiteboard?.toObject();
  if (!posterObject) {
    return;
  }

  if (isEditorMobileVariant()) {
    openNewDownloadModal({
      id: 'mobile-download-panel',
      posterObject: posterObject,
      isOpenedInEditor: true,
      isOpenedInModal: true,
      isOpenedInPrintShop: false,
    });
    return;
  }

  window.PMW.redux.store.dispatch(updateWebDownloadPanelOpenState(true));
};

export const openDownloadModal = (mode = DownloadPublishModalMode.DOWNLOAD, option = '', hashedId?: string): void => {
  if (window.posterEditor?.whiteboard?.drawing.isDrawModeOn) {
    window.posterEditor?.whiteboard?.drawing.updateFromObject({
      isDrawModeOn: false,
    });
  }

  window.posterEditor?.whiteboard?.deleteItemsNotVisibleOnPoster(false);
  window.PMW.openDownloadDialog({
    isPosterMaker: window.PMW.isPosterMaker() ? 1 : 0,
    preselectPaidProduct: window.PMW.isEmbedded() && isUserPremium(),
    panelToSelect: mode,
    option,
    idPosterHash: window.posterEditor?.whiteboard?.id !== undefined && Number(window.posterEditor?.whiteboard?.id) > 0 ? window.posterEditor?.whiteboard?.hashedID : hashedId || '',
    isPremium: window.posterEditor?.whiteboard?.getCurrentPage().isPremium() ? 1 : 0,
    posterType: window.posterEditor?.whiteboard?.type.name,
    idGalleryTemplate: window.posterEditor?.whiteboard?.getGalleryTemplateId(),
    idTemplate: window.posterEditor?.whiteboard?.getTemplateId(),
    uc: window.posterEditor?.whiteboard?.savePoster.hasUnsavedChanges() ? 1 : 0,
    isVideo: window.posterEditor?.whiteboard?.isVideo() ? 1 : 0,
    hasVideoItem: window.posterEditor?.whiteboard?.getCurrentPage().items.hasVideoItem() ? 1 : 0,
    isExpensiveVideo: window.posterEditor?.whiteboard?.getCurrentPage().isExpensiveVideo() ? 1 : 0,
    isTransparent: window.posterEditor?.whiteboard?.getCurrentPage().isTransparent() ? 1 : 0,
    hasClickableLink: window.posterEditor?.whiteboard?.getCurrentPage().items.hasClickableItem() ? 1 : 0,
    pw: window.posterEditor?.whiteboard?.width,
    ph: window.posterEditor?.whiteboard?.height,
    ptw: window.posterEditor?.whiteboard?.userWidth,
    pth: window.posterEditor?.whiteboard?.userHeight,
    ptu: window.posterEditor?.whiteboard?.units,
  });
};

export const onOpenUploadsMediaSlideshowModal = (onAddSelected: (items: ItemData[]) => void): void => {
  openUserMediaModal({
    onAddSelected,
    shouldLoadDataUrlImage: true,
    onAddBrandAsset: (): void => {
      void onAddBrandAssetClickedHandler();
    },
    showVectors: false,
    uploadAcceptedMimetype: 'image/jpeg,image/heic,image/heif,image/png,image/webp,image/gif,video/quicktime,video/mp4,video/x-m4v,video/*',
    defaultTab: UploadsMediaType.IMAGE,
    mediaTypesToShow: [UploadsMediaType.IMAGE, UploadsMediaType.VIDEO],
  });
};

export const onOpenFancyTextModal = (): void => {
  openFancytextModal({
    onAddSelected: (data: ItemData): void => {
      onAddItems([data]);
    },
  });
};

export const onOpenScheduleModal = (): void => {
  const data = getDefaultScheduleData();

  openScheduleModal({
    isNew: true,
    unusedData: data.unusedData,
    timeFormat: data.timeFormat,
    dateFormat: data.dateFormat,
    initialColumns: data.columns,
    columnsData: data.columnData,
    initialTableData: data.tableData,
    highlightedRows: data.highlightedRows,
    initialRows: DEFAULT_ROWS,
    layout: LayoutData.SLANTED_SPORTS_LAYOUT,
    onAddSchedule: addScheduleToPoster,
  });
};

export const onOpenMenuModal = (): void => {
  openUserMenuModal({
    layout: LayoutTypes.MENU_LAYOUT_1,
    selectedIds: [],
    selectedItems: [],
    onAddMenuItem: addMenuToPoster,
  });
};

const trimVideoCallback = async (item: VideoItem | VideoSlideItem, trimData: TrimData): Promise<void> => {
  if (item.isVideoSlide()) {
    const newSlideDuration = (trimData.endTime - trimData.startTime) / (item.speed ?? 1);
    await item.slideshow.updateSlideFromObject(
      item.uid,
      {
        ...trimData,
        slideDuration: newSlideDuration,
      },
      {checkForDurationUpdate: true}
    );
    await item.onItemTrimmed();
    return;
  }

  await item.updateFromObject(
    {
      ...trimData,
    },
    {checkForDurationUpdate: true}
  );
  await item.onItemTrimmed();
};

export const onOpenTrimVideoModal = (activeItem: VideoItem | VideoSlideItem, forceTrim = false, callback = (): void => {}): void => {
  openTrimVideoModal({
    isMuted: activeItem.isMuted(),
    scaledHeight: activeItem.fabricObject.getScaledHeight(),
    scaledWidth: activeItem.fabricObject.getScaledWidth(),
    duration: activeItem.duration,
    start: activeItem.startTime,
    end: activeItem.endTime,
    source: repoVideoURLForModel(activeItem),
    maxDuration: activeItem.duration,
    panelId: 'trim-video-modal',
    onTrim: async (trimData: TrimData): Promise<void> => {
      trackPosterBuilderGA4Events(GA4EventName.TRIM_VIDEO_MODAL, {
        [GA4EventParamName.TYPE]: isEditorMobileVariant() ? 'mobile' : 'web',
      });
      await trimVideoCallback(activeItem, trimData);
      callback();
    },
    forceTrim,
  });
};

export const onClickRemoveVideoBG = async (item: VideoItem | VideoSlideItem, remainingTeamCredits: number | null): Promise<void> => {
  void window.posterEditor?.whiteboard?.pause();

  if (item.removeBackground.isBackgroundRemoved && !item.removeBackground.isBackgroundBeingRemoved) {
    showMessageGrowl({
      text: window.i18next.t('pmwjs_item_background_is_removed'),
      type: GROWL_TYPE.NEUTRAL,
    });
    await item.disableRemoveBackgroundFlag();
    return;
  }

  if (remainingTeamCredits === null) {
    return;
  }

  const creditsToCharge = await item.removeBackground.getCreditsToChargeForBackgroundRemovalForUser();
  const isInsufficientCredits = remainingTeamCredits < creditsToCharge;
  if (!isPremiumUser() && isInsufficientCredits) {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_VIDEO_BG_REMOVALS);
    return;
  }

  if (creditsToCharge > 0 && !isInsufficientCredits) {
    void onOpenRemoveVideoBackgroundModal(item, creditsToCharge, remainingTeamCredits);
  } else if (isInsufficientCredits) {
    triggerBuyMoreCredits({
      headingText: window.i18next.t('pmwjs_credits_pack_on_background_removal', {
        creditsRemaining: remainingTeamCredits,
        creditsCost: creditsToCharge,
      }),
      cb: onCreditsPurchaseSuccess.bind(this, item),
    });
  } else {
    void onConfirmRemoveBG(item);
  }
};

export const onCreditsPurchaseSuccess = (item: VideoItem | VideoSlideItem, updatedCredits: number): void => {
  void refreshTeamCreditsInStore();
  void onClickRemoveVideoBG(item, updatedCredits);
};

export const onOpenRemoveVideoBackgroundModal = async (
  activeItem: VideoItem | VideoSlideItem,
  creditsToCharge: number,
  remainingBackgroundRemovalCredits: number
): Promise<void> => {
  openConfirmRemoveVideoBackgroundModal({
    onConfirm: onConfirmRemoveBG.bind(this, activeItem),
    remainingCredits: remainingBackgroundRemovalCredits,
    creditsToCharge,
    videoHashedFilename: activeItem.hashedFilename,
    videoFileExtension: activeItem.fileExtension,
    videoDuration: activeItem.getDuration(),
  });
};

// 16th April 2026 = Unscreen's API is no longer processing requests,
// so we have temporarily disabled the AI Video BG Removal option in
// the UI until a new API is integrated
export const openVideoBgRemovalTemporarilyDownMessageModel = (): void => {
  openMessageModal({
    title: window.i18next.t('pmwjs_video_background_removal_temporary_outage_title'),
    text: window.i18next.t('pmwjs_video_background_removal_temporary_outage_text'),
  });
};

export const onConfirmRemoveBG = async (item: VideoItem | VideoSlideItem): Promise<void> => {
  if (item.isVideoSlide()) {
    removeVideoSlideBackgroundFromId(item.slideshow.uid, item.uid);
  } else {
    removeVideoBackgroundFromId(item.uid);
  }
};

export const onOpenCropModal = (activeItem: ImageItem | ImageSlideItem): void => {
  trackPosterBuilderGA4Events(GA4EventName.CROP_ELEMENT, {[GA4EventParamName.TYPE]: 'image'});
  const onApplyCallback = (cropPanelResponse: CropperPanelApplyResponse): void => {
    if (activeItem.isImageSlide()) {
      void activeItem.slideshow.updateSlideFromObject(activeItem.uid, {
        cropData: {
          cropped: true,
          ...cropPanelResponse.cropData,
        },
        removeBackground: {
          isBackgroundRemoved: cropPanelResponse.isBackgroundRemoved,
        },
      });
      return;
    }

    void activeItem.updateFromObject({
      cropData: {
        cropped: true,
        ...cropPanelResponse.cropData,
      },
      removeBackground: {
        isBackgroundRemoved: cropPanelResponse.isBackgroundRemoved,
      },
    });
  };

  openCropperModal({
    onApply: onApplyCallback,
    imageHashedFilename: activeItem.hashedFilename,
    dataUrl: activeItem.uploadingImageData?.dataUrl,
    isInitiallyBackgroundRemoved: activeItem.removeBackground.isBackgroundRemoved,
    imageSource: activeItem.imageSource,
    initialCropData: activeItem.cropData,
    showRemoveBackgroundOption: activeItem.imageSource !== PMWStockImageSource.PMW_STOCK_IMAGE,
  });
};

export const onOpenReplaceBackground = (type: ReplaceMediaControlsButtons): void => {
  const openStockBackgroundModal = (): void => {
    openStockMediaModal({
      onAddSelected: addBackground,
      shouldLoadDataUrlImage: true,
      maxSelectionNumber: 1,
      tabsToShow: [StockPanelTabs.PHOTOS, StockPanelTabs.VIDEOS, StockPanelTabs.TEXT_TO_IMAGE],
      defaultSearchTab: StockPanelTabs.PHOTOS,
      showTextToImageTool: true,
      hideOnClose: false,
      panelId: 'STOCK_MEDIA_VIA_REPLACE_BACKGROUND',
    });
  };

  const openMyBackgroundsModal = (): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    const isVideoBg = currentPage ? isVideoBackground(currentPage.background.details) : false;
    openUserMediaModal({
      onAddSelected: addBackground,
      shouldLoadDataUrlImage: true,
      onAddBrandAsset: (): void => {
        void onAddBrandAssetClickedHandler();
      },
      mediaTypesToShow: [UploadsMediaType.IMAGE_BACKGROUND, UploadsMediaType.VIDEO_BACKGROUND],
      maxSelectionNumber: 1,
      showVectors: false,
      uploadAcceptedMimetype: 'image/jpeg,image/heic,image/heif,image/png,image/webp,image/gif,video/quicktime,video/mp4,video/x-m4v,video/*',
      defaultTab: isVideoBg ? UploadsMediaType.VIDEO_BACKGROUND : UploadsMediaType.IMAGE_BACKGROUND,
    });
  };

  switch (type) {
    case ReplaceMediaControlsButtons.EXPLORE_MEDIA: {
      openStockBackgroundModal();
      return;
    }
    case ReplaceMediaControlsButtons.MY_UPLOADS: {
      openMyBackgroundsModal();
      return;
    }
    default:
      throw new Error('unhandled type of id received from replace media controls');
  }
};

export const onOpenReplaceMedia = (
  type: ReplaceMediaControlsButtons,
  item: ImageItem | ImageSlideItem | VideoItem | VideoSlideItem,
  onReplace: (itemData: ItemData) => void
): void => {
  const onOpenStockModal = (): void => {
    openStockMediaModal({
      onAddSelected: (items: ElementData[]) => {
        onReplace(items[0]);
      },
      shouldLoadDataUrlImage: true,
      maxSelectionNumber: 1,
      tabsToShow: [StockPanelTabs.PHOTOS, StockPanelTabs.VIDEOS, StockPanelTabs.TEXT_TO_IMAGE],
      defaultSearchTab: item.isVideo() ? StockPanelTabs.VIDEOS : StockPanelTabs.PHOTOS,
      showTextToImageTool: true,
      hideOnClose: false,
      panelId: 'STOCK_PANEL_VIA_REPLACE_MEDIA',
    });
  };

  const openUploadsMediaModal = (): void => {
    openUserMediaModal({
      shouldLoadDataUrlImage: true,
      onAddSelected: (items: ElementData[]) => {
        onReplace(items[0]);
      },
      maxSelectionNumber: 1,
      onAddBrandAsset: (): void => {
        void onAddBrandAssetClickedHandler();
      },
      showVectors: !item.isSlideshowSlide(),
      uploadAcceptedMimetype: `${item.isSlideshowSlide() ? 'image/jpeg,image/heic,image/heif,image/png,image/webp,image/gif' : 'image/*,image/heic,image/heif'},video/quicktime,video/mp4,video/x-m4v,video/*`,
      defaultTab: item.isImage() || item.isImageSlide() ? UploadsMediaType.IMAGE : UploadsMediaType.VIDEO,
      mediaTypesToShow: [UploadsMediaType.IMAGE, UploadsMediaType.VIDEO],
    });
  };

  switch (type) {
    case ReplaceMediaControlsButtons.EXPLORE_MEDIA: {
      onOpenStockModal();
      return;
    }
    case ReplaceMediaControlsButtons.MY_UPLOADS: {
      openUploadsMediaModal();
      return;
    }
    default:
      throw new Error('unhandled type of id received from replace media controls');
  }
};

export const onOpenMaskingModal = async (item: ImageItem | ImageSlideItem): Promise<void> => {
  const imageUrl = await item.getImageUrl();
  const image = await loadImageAsync(imageUrl);

  const maskingModalCallback = (maskingObjects: MaskingObject[]): void => {
    trackPosterBuilderGA4Events(GA4EventName.MASK_APPLIED, {[GA4EventParamName.TYPE]: maskingObjects[0].type});
    void item.updateMasking(maskingObjects[0] ?? undefined);
  };

  // TODO: This code logic needs to be better; Right now the masking modal doesn't handle a different size image url too
  const removeBGCallback = async (isBackgroundRemoved: boolean, replaceImageCallback: (url: string) => void): Promise<boolean> => {
    if (isBackgroundRemoved) {
      const sourceImageURL = await item.getImageUrl();
      replaceImageCallback(sourceImageURL);
      return false;
    }
    const removedBGURL = await item.removeBackground.getBackgroundRemovedURLForCurrentImageURL();
    if (await doesPublicFileExistAsync(removedBGURL)) {
      replaceImageCallback(removedBGURL);
    } else {
      try {
        showLoading('removing-background-masking-panel');
        await item.removeBackground.removeBackground();
        replaceImageCallback(removedBGURL);
      } catch (e) {
        return false;
      } finally {
        hideLoading('removing-background-masking-panel');
      }
    }

    return true;
  };

  openMaskingModal({
    onMaskCTA: maskingModalCallback,
    initialMaskingData: item.masking.maskingItem?.toObject(),
    imageUrl,
    cropData: item.cropData.isCropped() ? item.cropData.getScaledCropData(image) : undefined,
    removeBackgroundCallback: removeBGCallback,
    isBackgroundRemoved: item.removeBackground.isBackgroundRemoved,
  });
};

export const openColorBackgroundPanel = (bgType: BACKGROUND_TYPE): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    throw new Error(`Current page does not exist`);
  }
  const {background} = currentPage;

  let primaryColor = DEFAULT_BACKGROUND_COLOR;
  let secondaryColor = '';
  let gradientType = GRADIENT_TYPE.LINEAR;

  if (isColorBackground(background.details)) {
    if (background.details.fill.fillType === FillTypes.SOLID) {
      primaryColor = rgbaToHex(background.details.fill.fillColor[0], false);
    }

    if (background.details.fill.hasGradientFill()) {
      primaryColor = rgbaToHex(background.details.fill.fillColor[0], false);
      secondaryColor = rgbaToHex(background.details.fill.fillColor[1], false);

      if (background.details.fill.hasRadialGradientFill()) {
        gradientType = GRADIENT_TYPE.RADIAL;
      }
    }
  }

  const getNewFillTypeFromObj = (obj: BackgroundColorType): FillTypes => {
    if (obj.backgroundType === BACKGROUND_TYPE.GRADIENT) {
      if (obj.gradientType === GRADIENT_TYPE.LINEAR) {
        return FillTypes.LINEAR_GRADIENT;
      }
      return FillTypes.RADIAL_GRADIENT;
    }

    return FillTypes.SOLID;
  };

  const getNewFillColorFromObj = (obj: BackgroundColorType): RGBA[] => {
    const newFillType = getNewFillTypeFromObj(obj);
    switch (newFillType) {
      case FillTypes.SOLID:
        return [hexToRgba(obj.primaryColor)];
      case FillTypes.LINEAR_GRADIENT:
      case FillTypes.RADIAL_GRADIENT:
        return [hexToRgba(obj.primaryColor), hexToRgba(obj.secondaryColor)];
      default:
        throw new Error(`expected fill type to be solid or gradient`);
    }
  };

  const onApplyBackground = (obj: BackgroundColorType): void => {
    void currentPage.background.updateFromObject({
      details: {
        type: BackgroundTypeName.COLOR,
        fill: {
          fillType: getNewFillTypeFromObj(obj),
          fillColor: getNewFillColorFromObj(obj),
        },
      },
    });
  };

  openColorBackgroundModal({
    primaryColor,
    secondaryColor: bgType === BACKGROUND_TYPE.GRADIENT && secondaryColor === '' ? lightenDarkenColor(hexToRgba(primaryColor), 100) : secondaryColor,
    onApplyChanges: onApplyBackground,
    bgType,
    gradientType,
  });
};

export const triggerBuyMoreCredits = (
  params: BuyMoreCreditsParams = {
    headingText: '',
    modalTitle: '',
    cb: (): void => {},
  }
): void => {
  const creditPurchaseCallBack = (data: BuyCreditsResponse): void => {
    params.cb(data.credits);
  };

  window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
    window.PMW.stripeCheckout.showStripeCheckoutForCreditPackPurchase(creditPurchaseCallBack, () => {}, params.headingText, params.modalTitle);
  });
};

export const onOpenVideoCropModal = (activeItem: VideoItem | VideoSlideItem, isFromMobile = false): void => {
  trackPosterBuilderGA4Events(GA4EventName.CROP_ELEMENT, {[GA4EventParamName.TYPE]: 'video'});

  const onApplyCallback = (videoCropperPanelResponse: VideoCropperPanelApplyResponse): void => {
    if (activeItem.isVideoSlide()) {
      void activeItem.slideshow.updateSlideFromObject(activeItem.uid, {
        cropData: {
          cropped: true,
          ...videoCropperPanelResponse.cropData,
        },
      });
      return;
    }

    void activeItem.updateFromObject({
      cropData: {
        cropped: true,
        ...videoCropperPanelResponse.cropData,
      },
    });
  };

  openVideoCropperModal({
    onApply: onApplyCallback,
    videoSrc: repoVideoURLForModel(activeItem),
    isFromMobile,
    initialCropData: activeItem.cropData?.cropped ? activeItem.cropData : null,
  });
};

export const onOpenVideoBackgroundCropModal = (isFromMobile = false): void => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }
  const currentPage = poster.getCurrentPage();
  const backgroundDetails = currentPage.background.details;
  if (!isVideoBackground(backgroundDetails)) {
    return;
  }

  const onApplyCallback = (videoCropperPanelResponse: VideoCropperPanelApplyResponse): void => {
    void currentPage.background.updateFromObject({
      details: {
        cropData: {
          cropped: true,
          ...videoCropperPanelResponse.cropData,
        },
      },
    });
  };

  openVideoCropperModal({
    onApply: onApplyCallback,
    videoSrc: repoVideoURLForModel(backgroundDetails),
    showCroppingPresets: false,
    aspectRatio: poster.width / poster.height,
    isFromMobile,
    initialCropData: backgroundDetails.cropData?.cropped ? backgroundDetails.cropData : null,
  });
};

export const openPreviewThumbnailSelectionModalOnSubmit = (): void => {
  const posterHashedId = window.posterEditor?.whiteboard?.hashedID ?? '';

  const onSubmitCallback = (): void => {
    void window.posterEditor?.whiteboard?.savePoster.save();
  };
  openMultipagePreviewThumbnailSelectionModal({posterHashedId, onSubmit: onSubmitCallback});
};
